import { englishPairScore } from "@/lib/tool-kit/ciphers-misc/english-ngrams";

/**
 * XOR as a cipher: each byte of the data is combined with a byte of the key, and the key
 * repeats when the data is longer.
 *
 * Text is handled as UTF-8 bytes, so "é" is two bytes and an emoji is four; the result of
 * enciphering is bytes, shown as hex or Base64 because most of them are not printable.
 * XOR is its own inverse — (a ⊕ k) ⊕ k = a — so one function encodes and decodes.
 */

const HEX_DIGITS = "0123456789abcdef";
const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const SEPARATORS = /[\s,:;-]+/g;
const HEX_PREFIX = /0x/gi;
const HEX_ONLY = /^[0-9a-f]*$/i;
const BASE64_PADDING = /=+$/;
const BASE64_SPACES = /\s+/g;
const BYTE_BITS = 8;
const BYTE_RANGE = 256;
const BYTE_MASK = 0xff;
const NIBBLE_SHIFT = 4;
const SEXTET_BITS = 6;

// biome-ignore-start lint/suspicious/noBitwiseOperators: XOR, hex and Base64 are defined on bits and bytes
export function textToBytes(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

/** UTF-8 bytes to text, and whether they were valid UTF-8 (invalid bytes become �). */
export function bytesToText(bytes: Uint8Array): {
  text: string;
  valid: boolean;
} {
  const text = new TextDecoder("utf-8").decode(bytes);
  let valid = true;
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    valid = false;
  }
  return { text, valid };
}

/** "0b 36 3f", "0B363F" or "0x0b,0x36" to bytes; null when it is not whole hex bytes. */
export function parseHexBytes(input: string): Uint8Array | null {
  const digits = input.replace(HEX_PREFIX, "").replace(SEPARATORS, "");
  if (!HEX_ONLY.test(digits) || digits.length % 2 !== 0) {
    return null;
  }
  const bytes = new Uint8Array(digits.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(digits.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

export function byteToHex(byte: number): string {
  return `${HEX_DIGITS[byte >> NIBBLE_SHIFT]}${HEX_DIGITS[byte & 0x0f]}`;
}

/** Bytes as lowercase hex pairs separated by spaces. */
export function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map(byteToHex).join(" ");
}

export function bytesToBase64(bytes: Uint8Array): string {
  let result = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const chunk =
      (bytes[index] << 16) |
      ((bytes[index + 1] ?? 0) << BYTE_BITS) |
      (bytes[index + 2] ?? 0);
    const left = bytes.length - index;
    result += BASE64_ALPHABET[(chunk >> 18) & 63];
    result += BASE64_ALPHABET[(chunk >> 12) & 63];
    result += left > 1 ? BASE64_ALPHABET[(chunk >> SEXTET_BITS) & 63] : "=";
    result += left > 2 ? BASE64_ALPHABET[chunk & 63] : "=";
  }
  return result;
}

/** Base64 (standard or URL-safe alphabet, padding optional) to bytes; null if invalid. */
export function parseBase64Bytes(input: string): Uint8Array | null {
  const body = input
    .replace(BASE64_SPACES, "")
    .replace(BASE64_PADDING, "")
    .replaceAll("-", "+")
    .replaceAll("_", "/");
  if (body.length % 4 === 1) {
    return null;
  }
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of body) {
    const value = BASE64_ALPHABET.indexOf(char);
    if (value < 0) {
      return null;
    }
    buffer = (buffer << SEXTET_BITS) | value;
    bits += SEXTET_BITS;
    if (bits >= BYTE_BITS) {
      bits -= BYTE_BITS;
      bytes.push((buffer >> bits) & BYTE_MASK);
      buffer &= (1 << bits) - 1;
    }
  }
  return Uint8Array.from(bytes);
}

/** XOR `data` with `key`, repeating the key. An empty key leaves the data as it is. */
export function xorBytes(data: Uint8Array, key: Uint8Array): Uint8Array {
  if (key.length === 0) {
    return Uint8Array.from(data);
  }
  return data.map((byte, index) => byte ^ key[index % key.length]);
}

/** A byte as eight binary digits. */
export function byteToBits(byte: number): string {
  return byte.toString(2).padStart(BYTE_BITS, "0");
}

export interface XorCandidate {
  key: number;
  text: string;
  score: number;
}

const CONTROL = /\p{Cc}/u;
const ALLOWED_CONTROL = new Set(["\t", "\n", "\r"]);
const LETTER_OR_SPACE = /[A-Za-z ]/g;

function hasControl(text: string): boolean {
  for (const char of text) {
    if (CONTROL.test(char) && !ALLOWED_CONTROL.has(char)) {
      return true;
    }
  }
  return false;
}

/**
 * Try all 256 single-byte keys and keep the readable results, best first. A result is
 * readable when it is valid UTF-8 without control characters; it is ranked by the share
 * of letters and spaces plus the English pair score. A hint on short data.
 */
export function crackSingleByte(data: Uint8Array): XorCandidate[] {
  const candidates: XorCandidate[] = [];
  for (let key = 0; key < BYTE_RANGE; key += 1) {
    const { text, valid } = bytesToText(xorBytes(data, Uint8Array.of(key)));
    if (valid && text.length > 0 && !hasControl(text)) {
      const share = (text.match(LETTER_OR_SPACE)?.length ?? 0) / text.length;
      candidates.push({
        key,
        text,
        score: share * 10 + englishPairScore(text),
      });
    }
  }
  return candidates.sort((a, b) => b.score - a.score || a.key - b.key);
}
// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the bit-level helpers
